
/**
 * PersonalDataCategory Integration
 * Syncs SDK Service with Frontend Store
 */
import i18next from 'i18next';
import { toast } from 'sonner';
import useKVKKCategoryStore from '@/store/kvkk-category.store.js';
import { createIntegrationLogger } from '@/lib/app-log.js';
import { fetchDropdownOptionsFromReference } from '@/lib/reference-dropdown.helper.js';
import { enrichPersonalDataCategoryRows } from '@/lib/personal-data-category-grouping.js';

export class PersonalDataCategoryIntegration {
    constructor(sdkService) {
        this.service = sdkService;
        this.log = createIntegrationLogger('PersonalDataCategory');
    }

    /**
     * Fetch all categories with params and sync store
     */
    async fetchAll(params = {}) {
        const store = useKVKKCategoryStore.getState();
        store.setLoading(true);
        try {
            const { data, pagination, uniqueValues } = await this.service.getAll(params);

            store.setCategories(data);
            if (pagination) store.setPaginationData(pagination);
            if (uniqueValues) store.setUniqueValues(uniqueValues);

            return { success: true, data };
        } catch (error) {
            this.log.error('FetchAll failed', { error });
            toast.error(error.message || i18next.t('integrationToasts:anErrorOccurredWhileLoadingData', { defaultValue: 'An error occurred while loading data' }));
            return { success: false, error: error.message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Get single category
     */
    async getById(id) {
        try {
            const { data } = await this.service.getById(id);
            return { success: true, data };
        } catch (error) {
            toast.error(error.message || i18next.t('integrationToasts:couldNotLoadCategoryDetails', { defaultValue: 'Could not load category details' }));
            return { success: false, error: error.message };
        }
    }

    /**
     * Create category
     */
    async create(data) {
        const store = useKVKKCategoryStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.create(data);
            toast.success(i18next.t('integrationToasts:categoryCreatedSuccessfully', { defaultValue: 'Category created successfully' }));
            await this.fetchAll(store.searchParams); // Refresh list
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(error.message || i18next.t('integrationToasts:couldNotCreateCategory', { defaultValue: 'Could not create category' }));
            return { success: false, error: error.message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update category
     */
    async update(id, data) {
        const store = useKVKKCategoryStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.update(id, data);
            toast.success(i18next.t('integrationToasts:categoryUpdatedSuccessfully', { defaultValue: 'Category updated successfully' }));
            await this.fetchAll(store.searchParams); // Refresh list
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(error.message || i18next.t('integrationToasts:couldNotUpdateCategory', { defaultValue: 'Could not update category' }));
            return { success: false, error: error.message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Single delete
     */
    async delete(id) {
        const store = useKVKKCategoryStore.getState();
        store.setLoading(true);
        try {
            await this.service.delete(id);
            toast.success(i18next.t('integrationToasts:categoryDeletedSuccessfully', { defaultValue: 'Category deleted successfully' }));
            await this.fetchAll(store.searchParams); // Refresh list
            return { success: true };
        } catch (error) {
            toast.error(error.message || i18next.t('integrationToasts:couldNotDeleteCategory', { defaultValue: 'Could not delete category' }));
            return { success: false, error: error.message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk delete
     */
    async bulkDelete(ids) {
        const store = useKVKKCategoryStore.getState();
        store.setLoading(true);
        try {
            await this.service.bulkDelete(ids);
            toast.success(i18next.t('integrationToasts:nCategoriesDeletedSuccessfully', {
                count: ids.length,
                defaultValue: `${ids.length} categories deleted successfully`,
            }));
            await this.fetchAll(store.searchParams); // Refresh list
            return { success: true };
        } catch (error) {
            toast.error(error.message || i18next.t('integrationToasts:deleteFailed', { defaultValue: 'Delete failed' }));
            return { success: false, error: error.message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Fetch dropdown data (reference cache with parent name enrichment).
     */
    async fetchDropdownData() {
        try {
            const result = await fetchDropdownOptionsFromReference('personalDataCategory', async () => {
                const { data } = await this.service.getDropdownParams();
                return { success: true, data: data || [] };
            });
            if (result.success && Array.isArray(result.data)) {
                return { ...result, data: enrichPersonalDataCategoryRows(result.data) };
            }
            return result;
        } catch (error) {
            this.log.error('FetchDropdown failed', { error });
            return { success: false, error: error.message };
        }
    }

    /**
     * PD inventory form: populated parentId from API (not reference-only cache).
     */
    async fetchDropdownDataForPdInventory() {
        try {
            const { data } = await this.service.getDropdownParams();
            return {
                success: true,
                data: enrichPersonalDataCategoryRows(data || []),
            };
        } catch (error) {
            this.log.error('FetchDropdownForPdInventory failed', { error });
            return { success: false, error: error.message, data: [] };
        }
    }
}
